import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Gift,
  Loader2,
  AlertCircle,
  CheckCircle,
  Plus,
  Save,
  X,
  Pencil,
  Code,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { authFetch } from '@/lib/auth-client';

// ============================================================================
// TYPES
// ============================================================================

interface RewardStats {
  claims: number;
  coinsAwarded: number;
}

interface Reward {
  id: string;
  rewardType: string;
  label: string;
  description: string | null;
  coinsAmount: number;
  cooldownMinutes: number;
  maxPerDay: number | null;
  maxPerUser: number | null;
  category: Category;
  isActive: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  stats: RewardStats;
}

const CATEGORIES = ['DAILY', 'ADS', 'SOCIAL', 'CONTENT', 'REFERRAL', 'MILESTONE'] as const;
type Category = (typeof CATEGORIES)[number];

const CATEGORY_COLORS: Record<Category, string> = {
  DAILY: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
  ADS: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
  SOCIAL: 'text-purple-400 bg-purple-500/10 border-purple-500/30',
  CONTENT: 'text-pink-400 bg-pink-500/10 border-pink-500/30',
  REFERRAL: 'text-green-400 bg-green-500/10 border-green-500/30',
  MILESTONE: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
};

// ============================================================================
// COOLDOWN HELPERS
// ============================================================================

function formatCooldown(minutes: number): string {
  if (minutes === 0) return 'None';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}h`;
  const days = Math.floor(minutes / (60 * 24));
  return `${days}d`;
}

// ============================================================================
// EDIT ROW
// ============================================================================

type EditState = {
  label: string;
  description: string;
  coinsAmount: number;
  cooldownMinutes: number;
  maxPerDay: string; // string for blank-as-null
  maxPerUser: string;
  category: Category;
  metadata: string; // JSON text
};

function toEditState(r: Reward): EditState {
  return {
    label: r.label,
    description: r.description ?? '',
    coinsAmount: r.coinsAmount,
    cooldownMinutes: r.cooldownMinutes,
    maxPerDay: r.maxPerDay === null ? '' : String(r.maxPerDay),
    maxPerUser: r.maxPerUser === null ? '' : String(r.maxPerUser),
    category: r.category,
    metadata: JSON.stringify(r.metadata ?? {}, null, 2),
  };
}

function RewardRow({
  reward,
  onUpdated,
}: {
  reward: Reward;
  onUpdated: (r: Reward) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [showMeta, setShowMeta] = useState(false);
  const [form, setForm] = useState<EditState>(() => toEditState(reward));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [metaError, setMetaError] = useState('');

  const startEdit = () => {
    setForm(toEditState(reward));
    setError('');
    setMetaError('');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError('');
    setMetaError('');
  };

  const save = async () => {
    setMetaError('');
    let metadata: Record<string, unknown> | null = null;
    try {
      metadata = form.metadata.trim() === '' ? null : JSON.parse(form.metadata);
    } catch {
      setMetaError('Metadata must be valid JSON');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const res = await authFetch(`/api/admin/rewards/${reward.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: form.label,
          description: form.description,
          coinsAmount: form.coinsAmount,
          cooldownMinutes: form.cooldownMinutes,
          maxPerDay: form.maxPerDay === '' ? null : Number(form.maxPerDay),
          maxPerUser: form.maxPerUser === '' ? null : Number(form.maxPerUser),
          category: form.category,
          metadata,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save');
      }
      const data = await res.json();
      onUpdated({ ...reward, ...data.data });
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await authFetch(`/api/admin/rewards/${reward.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !reward.isActive }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to toggle');
      }
      const data = await res.json();
      onUpdated({ ...reward, ...data.data });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const metadataUrl = (reward.metadata?.externalUrl as string | undefined) ?? null;

  return (
    <div
      className={cn(
        'rounded-lg border transition-colors',
        reward.isActive
          ? 'bg-zinc-900 border-zinc-800'
          : 'bg-zinc-900/40 border-zinc-800/50 opacity-60'
      )}
    >
      {/* Row header */}
      <div className="flex items-center gap-3 p-3">
        {/* Active toggle */}
        <button
          onClick={toggleActive}
          disabled={saving}
          className={cn(
            'relative h-6 w-11 rounded-full transition-colors flex-shrink-0 disabled:opacity-50',
            reward.isActive ? 'bg-green-500' : 'bg-zinc-700'
          )}
          aria-label={reward.isActive ? 'Deactivate reward' : 'Activate reward'}
        >
          <span
            className={cn(
              'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
              reward.isActive ? 'translate-x-5' : 'translate-x-0'
            )}
          />
        </button>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-white">{reward.label}</span>
            <code className="text-[10px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">
              {reward.rewardType}
            </code>
            <span
              className={cn(
                'text-[10px] font-medium px-1.5 py-0.5 rounded border',
                CATEGORY_COLORS[reward.category]
              )}
            >
              {reward.category}
            </span>
            {metadataUrl && (
              <a
                href={metadataUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-zinc-500 hover:text-zinc-300 flex items-center gap-0.5"
                title={metadataUrl}
              >
                <ExternalLink className="h-3 w-3" />
                link
              </a>
            )}
          </div>
          {reward.description && !editing && (
            <p className="text-xs text-zinc-500 mt-0.5 truncate">{reward.description}</p>
          )}
        </div>

        {/* Stats */}
        <div className="hidden md:flex items-center gap-4 text-xs text-zinc-400 flex-shrink-0">
          <div className="text-right">
            <div className="font-mono font-semibold text-amber-400">{reward.coinsAmount}c</div>
            <div className="text-[10px] text-zinc-600">per claim</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-zinc-300">{formatCooldown(reward.cooldownMinutes)}</div>
            <div className="text-[10px] text-zinc-600">cooldown</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-zinc-300">
              {reward.maxPerDay === null ? '∞' : reward.maxPerDay}
            </div>
            <div className="text-[10px] text-zinc-600">per day</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-zinc-300">
              {reward.maxPerUser === null ? '∞' : reward.maxPerUser}
            </div>
            <div className="text-[10px] text-zinc-600">lifetime</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-zinc-500">{reward.stats.claims}</div>
            <div className="text-[10px] text-zinc-600">claims</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {!editing ? (
            <button
              onClick={startEdit}
              className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
              title="Edit reward"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          ) : (
            <>
              <button
                onClick={save}
                disabled={saving}
                className="p-1.5 rounded text-green-400 hover:bg-green-500/10 transition-colors disabled:opacity-50"
                title="Save"
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                onClick={cancel}
                disabled={saving}
                className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
                title="Cancel"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="border-t border-zinc-800 p-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">Label</label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as Category }))}
                className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">
                Description
              </label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">
                Coins per claim
              </label>
              <input
                type="number"
                min={0}
                value={form.coinsAmount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, coinsAmount: Number(e.target.value) }))
                }
                className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">
                Cooldown (minutes)
              </label>
              <input
                type="number"
                min={0}
                value={form.cooldownMinutes}
                onChange={(e) =>
                  setForm((f) => ({ ...f, cooldownMinutes: Number(e.target.value) }))
                }
                className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
              <span className="text-[10px] text-zinc-600 mt-0.5 block">
                {formatCooldown(form.cooldownMinutes)}
              </span>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">
                Max per day <span className="text-zinc-600">(blank = ∞)</span>
              </label>
              <input
                type="number"
                min={0}
                value={form.maxPerDay}
                onChange={(e) => setForm((f) => ({ ...f, maxPerDay: e.target.value }))}
                className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">
                Max per user (lifetime) <span className="text-zinc-600">(blank = ∞)</span>
              </label>
              <input
                type="number"
                min={0}
                value={form.maxPerUser}
                onChange={(e) => setForm((f) => ({ ...f, maxPerUser: e.target.value }))}
                className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Metadata (collapsible) */}
          <div>
            <button
              type="button"
              onClick={() => setShowMeta((s) => !s)}
              className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-400 hover:text-white transition-colors"
            >
              <Code className="h-3 w-3" />
              Metadata (JSON)
              <span className="text-zinc-600">
                {showMeta ? 'hide' : 'show'}
              </span>
            </button>
            {showMeta && (
              <>
                <textarea
                  value={form.metadata}
                  onChange={(e) => setForm((f) => ({ ...f, metadata: e.target.value }))}
                  rows={5}
                  spellCheck={false}
                  className="mt-1.5 w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-xs font-mono focus:border-amber-500 focus:outline-none"
                  placeholder='{"icon": "twitter", "externalUrl": "https://..."}'
                />
                <p className="text-[10px] text-zinc-600 mt-1">
                  Recognized keys: icon, platform, externalUrl, actionLabel, streakDay, adNetwork
                </p>
              </>
            )}
            {metaError && <p className="text-[11px] text-red-400 mt-1">{metaError}</p>}
          </div>

          {error && (
            <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-xs">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CREATE REWARD MODAL / FORM
// ============================================================================

function CreateRewardForm({
  onCreated,
  onCancel,
}: {
  onCreated: (r: Reward) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    rewardType: '',
    label: '',
    description: '',
    coinsAmount: 10,
    cooldownMinutes: 0,
    maxPerDay: '' as string,
    maxPerUser: '' as string,
    category: 'SOCIAL' as Category,
    metadata: '{}',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    let metadata: Record<string, unknown>;
    try {
      metadata = form.metadata.trim() === '' ? {} : JSON.parse(form.metadata);
    } catch {
      setError('Metadata must be valid JSON');
      return;
    }

    if (!form.rewardType || !form.label) {
      setError('rewardType and label are required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const res = await authFetch('/api/admin/rewards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rewardType: form.rewardType,
          label: form.label,
          description: form.description || null,
          coinsAmount: form.coinsAmount,
          cooldownMinutes: form.cooldownMinutes,
          maxPerDay: form.maxPerDay === '' ? null : Number(form.maxPerDay),
          maxPerUser: form.maxPerUser === '' ? null : Number(form.maxPerUser),
          category: form.category,
          isActive: true,
          metadata,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create');
      }
      const data = await res.json();
      onCreated({ ...data.data, stats: { claims: 0, coinsAwarded: 0 } });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Plus className="h-4 w-4 text-amber-400" />
          New Reward Definition
        </h3>
        <button
          onClick={onCancel}
          className="text-zinc-400 hover:text-white"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="block text-[11px] font-medium text-zinc-400 mb-1">
            Reward Type (stable key)
          </label>
          <input
            type="text"
            value={form.rewardType}
            onChange={(e) =>
              setForm((f) => ({ ...f, rewardType: e.target.value.toUpperCase() }))
            }
            placeholder="SOCIAL_FOLLOW_TWITCH"
            className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white font-mono text-sm focus:border-amber-500 focus:outline-none"
          />
          <span className="text-[10px] text-zinc-600 mt-0.5 block">
            Uppercase + underscores. Cannot be changed later.
          </span>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-zinc-400 mb-1">Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as Category }))}
            className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-3">
          <label className="block text-[11px] font-medium text-zinc-400 mb-1">Label</label>
          <input
            type="text"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="Follow on Twitch"
            className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
          />
        </div>

        <div className="md:col-span-3">
          <label className="block text-[11px] font-medium text-zinc-400 mb-1">Description</label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-zinc-400 mb-1">
            Coins per claim
          </label>
          <input
            type="number"
            min={0}
            value={form.coinsAmount}
            onChange={(e) => setForm((f) => ({ ...f, coinsAmount: Number(e.target.value) }))}
            className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-zinc-400 mb-1">
            Cooldown (min)
          </label>
          <input
            type="number"
            min={0}
            value={form.cooldownMinutes}
            onChange={(e) =>
              setForm((f) => ({ ...f, cooldownMinutes: Number(e.target.value) }))
            }
            className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-medium text-zinc-400 mb-1">Max/day</label>
            <input
              type="number"
              min={0}
              value={form.maxPerDay}
              onChange={(e) => setForm((f) => ({ ...f, maxPerDay: e.target.value }))}
              placeholder="∞"
              className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-zinc-400 mb-1">Max/user</label>
            <input
              type="number"
              min={0}
              value={form.maxPerUser}
              onChange={(e) => setForm((f) => ({ ...f, maxPerUser: e.target.value }))}
              placeholder="∞"
              className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
            />
          </div>
        </div>

        <div className="md:col-span-3">
          <label className="block text-[11px] font-medium text-zinc-400 mb-1">
            Metadata (JSON)
          </label>
          <textarea
            value={form.metadata}
            onChange={(e) => setForm((f) => ({ ...f, metadata: e.target.value }))}
            rows={3}
            spellCheck={false}
            className="w-full px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-xs font-mono focus:border-amber-500 focus:outline-none"
            placeholder='{"icon": "twitter", "platform": "twitter"}'
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-xs">
          <AlertCircle className="h-3 w-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 rounded-lg text-zinc-300 hover:bg-zinc-800 text-sm"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={saving}
          className="px-3 py-1.5 rounded-lg bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors text-sm disabled:opacity-50 flex items-center gap-1.5"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          Create Reward
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// PAGE
// ============================================================================

export default function AdminRewardsPage() {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState<Category | 'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await authFetch('/api/admin/rewards');
      if (!res.ok) throw new Error('Failed to load rewards');
      const data = await res.json();
      setRewards(data.rewards ?? data.data?.rewards ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpdated = useCallback((updated: Reward) => {
    setRewards((rs) => rs.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  }, []);

  const handleCreated = useCallback((created: Reward) => {
    setRewards((rs) => [...rs, created]);
    setShowCreate(false);
  }, []);

  // Totals for summary
  const summary = useMemo(() => {
    const active = rewards.filter((r) => r.isActive);
    const maxWeekly = active.reduce((sum, r) => {
      if (r.maxPerDay !== null) return sum + r.maxPerDay * 7 * r.coinsAmount;
      // No cap: assume 1 claim/week for one-time, else cooldown-bounded
      if (r.maxPerUser !== null) return sum; // one-time, don't recurring-count
      const weekly = r.cooldownMinutes > 0
        ? Math.floor((60 * 24 * 7) / r.cooldownMinutes) * r.coinsAmount
        : 7 * r.coinsAmount;
      return sum + weekly;
    }, 0);
    return {
      total: rewards.length,
      active: active.length,
      inactive: rewards.length - active.length,
      maxWeekly,
    };
  }, [rewards]);

  const filtered = useMemo(() => {
    if (filter === 'ALL') return rewards;
    if (filter === 'ACTIVE') return rewards.filter((r) => r.isActive);
    if (filter === 'INACTIVE') return rewards.filter((r) => !r.isActive);
    return rewards.filter((r) => r.category === filter);
  }, [rewards, filter]);

  const grouped = useMemo(() => {
    const by: Record<Category, Reward[]> = {
      DAILY: [],
      ADS: [],
      SOCIAL: [],
      CONTENT: [],
      REFERRAL: [],
      MILESTONE: [],
    };
    for (const r of filtered) by[r.category].push(r);
    return by;
  }, [filtered]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <Gift className="h-4 w-4 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Reward Management</h1>
            <p className="text-sm text-zinc-400">
              Configure coin-earning actions for the user rewards page
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate((s) => !s)}
          className="px-3 py-2 rounded-lg bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors text-sm flex items-center gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          New Reward
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 rounded-xl bg-zinc-900 border border-zinc-800">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Total</div>
          <div className="text-2xl font-bold text-white">{summary.total}</div>
        </div>
        <div className="p-3 rounded-xl bg-zinc-900 border border-zinc-800">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Active</div>
          <div className="text-2xl font-bold text-green-400">{summary.active}</div>
        </div>
        <div className="p-3 rounded-xl bg-zinc-900 border border-zinc-800">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Inactive</div>
          <div className="text-2xl font-bold text-zinc-500">{summary.inactive}</div>
        </div>
        <div className="p-3 rounded-xl bg-zinc-900 border border-zinc-800">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            Max weekly giveaway
          </div>
          <div className="text-2xl font-bold text-amber-400">
            {summary.maxWeekly.toLocaleString()}c
          </div>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <CreateRewardForm onCreated={handleCreated} onCancel={() => setShowCreate(false)} />
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['ALL', 'ACTIVE', 'INACTIVE', ...CATEGORIES] as const).map((key) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={cn(
              'px-3 py-1 rounded-lg text-xs font-medium transition-colors',
              filter === key
                ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800 border border-transparent'
            )}
          >
            {key}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Rewards list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 text-amber-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-zinc-700" />
          <p>No rewards match this filter.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {CATEGORIES.map((cat) => {
            const list = grouped[cat];
            if (!list || list.length === 0) return null;
            return (
              <div key={cat} className="space-y-2">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'text-[11px] font-semibold px-2 py-0.5 rounded border',
                      CATEGORY_COLORS[cat]
                    )}
                  >
                    {cat}
                  </span>
                  <span className="text-[11px] text-zinc-500">
                    {list.length} reward{list.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {list.map((r) => (
                    <RewardRow key={r.id} reward={r} onUpdated={handleUpdated} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
